import React, { useState, useEffect } from 'react';
import { database } from '../supabase';
import { CartItem } from '../hooks/useCart';
import ProductModal from './ProductModal';
import './Shop.css';

interface Product {
  id: string;
  name: string;
  description?: string;
  price: string;
  images: string[];
  size: string;
  category?: string;
  stock_quantity?: number;
  tags?: string[];
  created_at?: string;
}

interface ShopProps {
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onAddToCart: (item: Omit<CartItem, 'quantity'>) => void;
  category?: string;
  newArrivals?: boolean;
}

const Shop: React.FC<ShopProps> = ({ searchTerm, onSearchChange, onAddToCart, category = "all", newArrivals = false }) => {
  const [activeFilters, setActiveFilters] = useState({
    era: 'all',
    type: 'all',
    priceRange: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(48);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hoveredProductId, setHoveredProductId] = useState<string | null>(null);

  const handleMouseEnter = (productId: string) => {
    setHoveredProductId(productId);
  };

  const handleMouseLeave = () => {
    setHoveredProductId(null);
  };

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  const normalizeCategory = (value?: string) => {
    const normalizedValue = value?.trim().toLowerCase();

    if (!normalizedValue) {
      return '';
    }

    if (normalizedValue === 'band-tees' || normalizedValue === 'band tees' || normalizedValue === 'tee' || normalizedValue === 'tees') {
      return 'teeshirts';
    }

    return normalizedValue;
  };

  // Load products from Supabase - runs when component mounts AND when newArrivals changes
  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log('🔍 Shop: Loading products from database');
        const { data, error } = await database.getProducts();

        if (error) {
          console.error('❌ Shop: Error loading products:', error);
          setError(`Failed to load products: ${error.message}`);
          setProducts([]);
          return;
        }

        if (data) {
          console.log('✅ Shop: Received', data.length, 'products');
          
          let transformedProducts: Product[] = data.map((product: any) => ({
            id: product.id,
            name: product.name,
            description: product.description,
            price: `$${product.price.toFixed(2)}`,
            images: product.images || ['/images/placeholder.jpg'],
            size: product.size || 'SIZE: ONE SIZE',
            category: product.category,
            stock_quantity: product.stock_quantity,
            tags: product.tags,
            created_at: product.created_at
          }));

          // Filter for new arrivals
          if (newArrivals) {
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            
            transformedProducts = transformedProducts.filter((product: any) => {
              if (product.created_at) {
                const createdDate = new Date(product.created_at);
                return createdDate >= thirtyDaysAgo;
              }
              return false;
            });
            
            if (transformedProducts.length < 50) {
              transformedProducts = data.slice(0, 50).map((product: any) => ({
                id: product.id,
                name: product.name,
                description: product.description,
                price: `$${product.price.toFixed(2)}`,
                images: product.images || ['/images/placeholder.jpg'],
                size: product.size || 'SIZE: ONE SIZE',
                category: product.category,
                stock_quantity: product.stock_quantity,
                tags: product.tags,
                created_at: product.created_at
              }));
            }
          }

          setProducts(transformedProducts);
        } else {
          setProducts([]);
        }
      } catch (err) {
        console.error('❌ Shop: Unexpected error:', err);
        setError('Failed to load products');
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [newArrivals]);

  const filteredProducts = products.filter(product => {
    const normalizedProductCategory = normalizeCategory(product.category);
    const normalizedPageCategory = normalizeCategory(category);
    const searchableFields = [
      product.name,
      product.size,
      product.description,
      normalizedProductCategory,
      ...(product.tags || [])
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    const matchesCategory = category === 'all' ||
      (normalizedPageCategory === 'teeshirts' && normalizedProductCategory === 'teeshirts') ||
      (normalizedPageCategory === 'archive' && normalizedProductCategory !== 'teeshirts') ||
      normalizedProductCategory === normalizedPageCategory;
    const matchesSearch = normalizedSearchTerm === '' || searchableFields.includes(normalizedSearchTerm);
    const matchesEra = activeFilters.era === 'all' ||
      (activeFilters.era === '1990s' && searchableFields.includes('1990s')) ||
      (activeFilters.era === '1980s' && searchableFields.includes('1980s')) ||
      (activeFilters.era === '2000s' && searchableFields.includes('2000s'));
    const matchesType = activeFilters.type === 'all' ||
      (activeFilters.type === 'jeans' && (normalizedProductCategory === 'jeans' || searchableFields.includes('jeans'))) ||
      (activeFilters.type === 'tees' && (normalizedProductCategory === 'teeshirts' || searchableFields.includes('tee') || searchableFields.includes('t-shirt')));
    const priceNum = parseFloat(product.price.replace('$', '').replace(',', ''));
    const matchesPrice = activeFilters.priceRange === 'all' ||
      (activeFilters.priceRange === 'under100' && priceNum < 100) ||
      (activeFilters.priceRange === '100to200' && priceNum >= 100 && priceNum <= 200) ||
      (activeFilters.priceRange === 'over200' && priceNum > 200);

    return matchesCategory && matchesSearch && matchesEra && matchesType && matchesPrice;
  });

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeFilters, searchTerm]);

  const handleFilterChange = (filterType: string, value: string) => {
    setActiveFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const clearFilters = () => {
    setActiveFilters({
      era: 'all',
      type: 'all',
      priceRange: 'all'
    });
    setCurrentPage(1);
    onSearchChange('');
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button key={i} className={`page-btn ${i === currentPage ? 'active' : ''}`} onClick={() => handlePageChange(i)}>
          {i}
        </button>
      );
    }
    return pages;
  };

  if (loading) {
    return (
      <div className="shop">
        <div className="shop-header">
          <h1>{newArrivals ? 'NEW ARRIVALS' : category === 'teeshirts' ? 'BAND-TEES' : 'ARCHIVE'}</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <h2>Loading products...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shop">
        <div className="shop-header">
          <h1>{newArrivals ? 'NEW ARRIVALS' : category === 'teeshirts' ? 'BAND-TEES' : 'ARCHIVE'}</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <h2>Error loading products</h2>
          <p>{error}</p>
          <button className="add-to-cart-btn" onClick={() => window.location.reload()} style={{ marginTop: '20px' }}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="shop">
      <div className="shop-header">
        <h1>
          {newArrivals
            ? 'NEW ARRIVALS'
            : category && category !== 'all'
            ? (category === 'teeshirts' ? 'BAND-TEES' : category.toUpperCase())
            : 'ARCHIVE'}
        </h1>
        <div className="filter-section">
          <div className="filters">
            <select value={activeFilters.era} onChange={(e) => handleFilterChange('era', e.target.value)} className="filter-select">
              <option value="all">All Eras</option>
              <option value="1980s">1980s</option>
              <option value="1990s">1990s</option>
              <option value="2000s">2000s</option>
            </select>
            <select value={activeFilters.type} onChange={(e) => handleFilterChange('type', e.target.value)} className="filter-select">
              <option value="all">All Types</option>
              <option value="jeans">Jeans</option>
              <option value="tees">Tees</option>
            </select>
            <select value={activeFilters.priceRange} onChange={(e) => handleFilterChange('priceRange', e.target.value)} className="filter-select">
              <option value="all">All Prices</option>
              <option value="under100">Under $100</option>
              <option value="100to200">$100 - $200</option>
              <option value="over200">Over $200</option>
            </select>
          </div>
        </div>
      </div>

      <div className="products-grid">
        {paginatedProducts.map((product, index) => {
          const currentImage = hoveredProductId === product.id && product.images[1] ? product.images[1] : product.images[0];
          const isFeatured = (index + 1) % 6 === 0 || (index + 1) % 11 === 0 || (index + 1) % 17 === 0;
          const itemClass = `product-item ${isFeatured ? 'featured' : ''}`;

          return (
            <div key={product.id} className={itemClass} onClick={() => handleProductClick(product)}
              onMouseEnter={() => handleMouseEnter(product.id)} onMouseLeave={handleMouseLeave}>
              <div className="product-image-wrapper">
                <img src={currentImage} alt={product.name} className="product-image" />
              </div>
              <div className="product-details">
                <h3 className="product-name">{product.name}</h3>
                <p className="product-size">{product.size}</p>
                <p className="product-price">{product.price}</p>
                <button className="add-to-cart-btn" onClick={(e) => { e.stopPropagation(); onAddToCart({ id: product.id, name: product.name, price: product.price, image: product.images[0] }); }}>
                  ADD TO CART
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <ProductModal product={selectedProduct} isOpen={isModalOpen} onClose={handleCloseModal} onAddToCart={onAddToCart} />

      {filteredProducts.length === 0 && (
        <div className="no-results">
          <h3>No products match your search criteria</h3>
          <p>Try adjusting your search or filters</p>
          {(normalizedSearchTerm !== '' || activeFilters.era !== 'all' || activeFilters.type !== 'all' || activeFilters.priceRange !== 'all') && (
            <button className="add-to-cart-btn" onClick={clearFilters} style={{ marginTop: '20px' }}>
              Clear Search And Filters
            </button>
          )}
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-controls">
            <div className="items-per-page">
              <label>Show:</label>
              <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="items-select">
                <option value={12}>12</option>
                <option value={24}>24</option>
                <option value={48}>48</option>
                <option value={96}>96</option>
              </select>
              <span>per page</span>
            </div>
            <div className="page-navigation">
              <button className="nav-btn prev-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>‹ PREV</button>
              {renderPageNumbers()}
              <button className="nav-btn next-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>NEXT ›</button>
            </div>
            <div className="page-info">
              Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredProducts.length)} of {filteredProducts.length} products
            </div>
          </div>
        </div>
      )}

      <div className="newsletter">
        <h2>SIGN UP FOR OUR NEWSLETTER FOR EXCLUSIVE PROMOS</h2>
        <div className="newsletter-form">
          <input type="email" placeholder="ENTER EMAIL" className="newsletter-email" />
          <button className="signup-btn" onClick={() => alert('Thank you for subscribing! You will receive exclusive promotions soon.')}>
            SIGN UP
          </button>
        </div>
      </div>
    </div>
  );
};

export default Shop;
